<template>
  <div class="admission-page">

    <!-- Loading -->
    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
      <p>Loading admission information…</p>
    </div>

    <template v-else>
      <!-- Header Banner -->
      <div class="header-banner">
        <h1>{{ headerText }}</h1>
      </div>

      <!-- Main Rich Content from API -->
      <div v-if="introHtml" class="content-body">
        <div class="rich-text" v-html="introHtml"></div>
      </div>

      <!-- Sections from API (if any) -->
      <div v-for="(section, i) in sections" :key="section.id || i" class="content-body">
        <div v-if="section.title" class="section-heading">
          <h2>{{ section.title }}</h2>
          <div class="heading-bar"></div>
        </div>
        <div v-if="section.body" class="rich-text" v-html="section.body"></div>
        <img v-if="section.image" :src="section.image" :alt="section.title" class="section-img" />
      </div>

      <!-- Grade Equivalency Tables (if populated) -->
      <template v-if="primaryGrades.length || secondaryGrades.length">
        <div class="content-body">
          <div class="section-heading">
            <h2>YEAR GROUP EQUIVALENCY</h2>
            <div class="heading-bar"></div>
          </div>

          <div v-if="primaryGrades.length" class="table-wrap">
            <h3 class="table-label">Primary</h3>
            <table class="eq-table">
              <thead>
                <tr>
                  <th>Age</th>
                  <th>Year Group</th>
                  <th>Grade System</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(g, j) in primaryGrades" :key="'p' + j">
                  <td>{{ g.age }}</td>
                  <td>{{ g.ukYear }}</td>
                  <td>{{ g.usGrade }}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div v-if="secondaryGrades.length" class="table-wrap">
            <h3 class="table-label">Secondary</h3>
            <table class="eq-table">
              <thead>
                <tr>
                  <th>Age</th>
                  <th>Year Group</th>
                  <th>Grade System</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(g, j) in secondaryGrades" :key="'s' + j">
                  <td>{{ g.age }}</td>
                  <td>{{ g.ukYear }}</td>
                  <td>{{ g.usGrade }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </template>
    </template>
  </div>
</template>

<script>
import api from '@/services/api';
import { mediaUrl, resolveRichText } from '@/services/media';

export default {
  name: 'AdmissionContent',
  data() {
    return {
      loading: true,
      headerText: 'THANK YOU FOR CONSIDERING THE ALSHOLA AMERICAN SCHOOL FOR THE EDUCATION OF YOUR CHILD',
      introHtml: '',
      sections: [],
      primaryGrades: [],
      secondaryGrades: [],
    }
  },
  async created() {
    try {
      let data;
      try {
        data = await api.getAdmissionPage();
      } catch (_) {
        const [secs, pGrades, sGrades] = await Promise.all([
          api.getAdmissionSections().catch(() => []),
          api.getGradeEquivalencies('primary').catch(() => []),
          api.getGradeEquivalencies('secondary').catch(() => []),
        ]);
        data = {
          sections: api.extractList(secs),
          grade_equivalency_primary: api.extractList(pGrades),
          grade_equivalency_secondary: api.extractList(sGrades),
        };
      }

      if (data) {
        // Header
        if (data.header_text) this.headerText = data.header_text;
        if (data.hero_title) document.title = data.hero_title + ' | Al Shola School';

        // Intro rich text (main content from CKEditor)
        if (data.intro_text) {
          this.introHtml = resolveRichText(data.intro_text);
        }

        // Sections (each may have rich text body)
        if (data.sections && data.sections.length > 0) {
          this.sections = data.sections.map(s => ({
            id: s.id,
            title: s.title || '',
            body: resolveRichText(s.body || s.content || ''),
            image: s.image ? mediaUrl(s.image) : '',
          }));
        }

        // Grade equivalency tables
        const mapGrades = (list) => api.extractList(list).map(g => ({
          age: g.age_range || g.age || '',
          ukYear: g.uk_year_group || '',
          usGrade: g.us_grade_system || g.us_grade || '',
        }));
        if (data.grade_equivalency_primary && data.grade_equivalency_primary.length > 0) {
          this.primaryGrades = mapGrades(data.grade_equivalency_primary);
        }
        if (data.grade_equivalency_secondary && data.grade_equivalency_secondary.length > 0) {
          this.secondaryGrades = mapGrades(data.grade_equivalency_secondary);
        }
      }
    } catch (e) {
      console.warn('[Admission] API failed:', e.message);
    } finally {
      this.loading = false;
    }
  }
}
</script>

<style scoped>
.admission-page {
  max-width: 960px;
  margin: 0 auto;
  padding: 0 0 60px;
  background: #fff;
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
  color: #333;
}

/* Loading */
.loading-state { display: flex; flex-direction: column; align-items: center; padding: 80px 20px; gap: 16px; color: #888; }
.spinner { width: 40px; height: 40px; border: 3px solid #eee; border-top-color: #C02130; border-radius: 50%; animation: spin 0.8s linear infinite; }
@keyframes spin { to { transform: rotate(360deg); } }

/* Header Banner */
.header-banner {
  background: linear-gradient(135deg, #C02130 0%, #8B1722 100%);
  color: white;
  padding: 36px 40px;
  margin-bottom: 0;
}
.header-banner h1 {
  font-size: clamp(18px, 3vw, 26px);
  font-weight: 700;
  margin: 0;
  line-height: 1.4;
  letter-spacing: 0.3px;
}

/* Content Body */
.content-body {
  padding: 32px 40px;
}

/* Section Headings */
.section-heading { margin-bottom: 20px; }
.section-heading h2 {
  color: #C02130;
  font-size: 22px;
  font-weight: 700;
  margin: 0 0 6px;
  text-transform: uppercase;
}
.heading-bar { height: 3px; background: #ff9800; width: 100%; border-radius: 2px; }

.section-img { max-width: 100%; border-radius: 12px; margin-top: 16px; }

/* ══════════ Rich Text (CKEditor HTML) ══════════ */
.rich-text {
  font-size: 16px;
  line-height: 1.75;
  color: #333;
}

/* Headings inside rich text */
.rich-text :deep(h1),
.rich-text :deep(h2),
.rich-text :deep(h3),
.rich-text :deep(h4) {
  color: #1a1a1a;
  margin: 28px 0 12px;
  line-height: 1.35;
}
.rich-text :deep(h1) { font-size: 28px; }
.rich-text :deep(h2) { font-size: 24px; color: #C02130; }
.rich-text :deep(h3) { font-size: 20px; }
.rich-text :deep(h4) { font-size: 17px; }

/* Paragraphs */
.rich-text :deep(p) {
  margin-bottom: 14px;
  text-align: justify;
}
/* Remove excessive empty paragraphs */
.rich-text :deep(p:empty),
.rich-text :deep(p:has(> br:only-child)) {
  margin-bottom: 4px;
}

/* Underlined headings from CKEditor */
.rich-text :deep(u) {
  text-decoration-color: #ff9800;
  text-underline-offset: 4px;
}

/* Bold text */
.rich-text :deep(strong) {
  font-weight: 700;
  color: #1a1a1a;
}

/* Lists */
.rich-text :deep(ol),
.rich-text :deep(ul) {
  margin: 16px 0;
  padding-left: 24px;
}
.rich-text :deep(ol) {
  list-style: none;
  counter-reset: ol-counter;
  padding-left: 0;
}
.rich-text :deep(ol > li) {
  counter-increment: ol-counter;
  margin-bottom: 20px;
  padding: 16px 20px;
  background: linear-gradient(135deg, #fafafa, #f5f5f5);
  border-radius: 10px;
  border-left: 4px solid #C02130;
}
.rich-text :deep(ol > li::before) {
  content: counter(ol-counter) ".";
  font-weight: 800;
  color: #C02130;
  font-size: 18px;
  margin-right: 10px;
}
.rich-text :deep(ol > li p) {
  display: inline;
}

.rich-text :deep(ul > li) {
  margin-bottom: 8px;
  padding-left: 4px;
}

/* Tables */
.rich-text :deep(table) {
  width: 100%;
  border-collapse: collapse;
  margin: 24px 0;
  font-size: 14px;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 2px 12px rgba(0,0,0,0.06);
}
.rich-text :deep(table thead tr:first-child td),
.rich-text :deep(table thead tr:first-child th),
.rich-text :deep(table tr:first-child td:has(strong)),
.rich-text :deep(table tbody tr:first-child td:has(strong)) {
  background: #C02130 !important;
  color: white !important;
  font-weight: 700;
  padding: 12px 14px;
  text-align: center;
}
.rich-text :deep(table tbody tr:first-child td:has(strong) strong),
.rich-text :deep(table tbody tr:first-child td:has(strong) span) {
  color: white !important;
}
.rich-text :deep(th) {
  background: #C02130;
  color: white;
  padding: 12px 14px;
  font-weight: 700;
  text-align: center;
  border: 1px solid #a01a26;
}
.rich-text :deep(td) {
  padding: 10px 14px;
  text-align: center;
  border: 1px solid #e8e8e8;
  vertical-align: middle;
}
.rich-text :deep(tbody tr:nth-child(even)) { background: #f9f9f9; }
.rich-text :deep(tbody tr:nth-child(odd)) { background: #fff; }
.rich-text :deep(tbody tr:hover) { background: #FFF5F5; }

/* RTL text */
.rich-text :deep([dir="RTL"]),
.rich-text :deep([style*="direction:rtl"]) {
  direction: rtl;
  text-align: right;
  font-family: 'Arial', sans-serif;
}

/* Images in rich text */
.rich-text :deep(img) {
  max-width: 100%;
  height: auto;
  border-radius: 10px;
  margin: 16px 0;
}

/* Figures (table wrappers from CKEditor) */
.rich-text :deep(figure.table) {
  margin: 24px 0;
  float: none !important;
  width: 100% !important;
}

/* Links */
.rich-text :deep(a) {
  color: #C02130;
  text-decoration: underline;
  text-decoration-color: rgba(192,33,48,0.3);
  text-underline-offset: 3px;
  transition: color 0.2s;
}
.rich-text :deep(a:hover) { color: #8B1722; }

/* ══════════ Standalone Grade Tables ══════════ */
.table-wrap { margin: 24px 0; }
.table-label { font-size: 16px; font-weight: 700; color: #1E3A8A; margin: 0 0 10px; }

.eq-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 15px;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 2px 12px rgba(0,0,0,0.06);
}
.eq-table thead { background: #C02130; color: white; }
.eq-table th { padding: 12px; text-align: center; font-weight: 700; border: 1px solid #a01a26; }
.eq-table td { padding: 10px; text-align: center; border: 1px solid #e8e8e8; }
.eq-table tbody tr:nth-child(even) { background: #f9f9f9; }
.eq-table tbody tr:hover { background: #FFF5F5; }

/* ══════════ Responsive ══════════ */
@media (max-width: 768px) {
  .header-banner { padding: 24px 20px; }
  .header-banner h1 { font-size: 17px; }
  .content-body { padding: 24px 20px; }
  .rich-text { font-size: 15px; }
  .rich-text :deep(table) { font-size: 12px; }
  .rich-text :deep(th), .rich-text :deep(td) { padding: 8px 6px; }
  .rich-text :deep(figure.table) { overflow-x: auto; }
}

@media (max-width: 480px) {
  .header-banner { padding: 20px 16px; }
  .content-body { padding: 20px 16px; }
  .rich-text :deep(ol > li) { padding: 12px 14px; }
}
</style>
